import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../../WebDevelopmentPage/components/AnimatedSection";
import { hmsModules, ModuleFeature } from "../hmsModulesData";
import HmsIcon from "../hmsIcons";

const FeatureBlock: React.FC<{ feature: ModuleFeature }> = ({ feature }) => (
  <div className="hms-module-feature">
    <div className="hms-module-feature__head">
      <span className="hms-module-feature__icon">
        <HmsIcon name={feature.icon} />
      </span>
      <h4 className="hms-module-feature__title">{feature.title}</h4>
    </div>
    {feature.description && <p className="hms-module-feature__desc">{feature.description}</p>}
    {feature.items && (
      <ul className="hms-module-feature__list">
        {feature.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    )}
    {feature.subsections?.map((sub) => (
      <div className="hms-module-feature__sub" key={sub.title}>
        <h5>{sub.title}</h5>
        <ul className="hms-module-feature__list">
          {sub.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const HmsModulesSection: React.FC = () => {
  const [activeId, setActiveId] = useState(hmsModules[0].id);
  const activeModule = hmsModules.find((m) => m.id === activeId) ?? hmsModules[0];

  return (
    <section className="hms-modules" aria-labelledby="hms-modules-title">
      <div className="container">
        <AnimatedSection className="wdev-section-head wdev-section-head--center">
          <h2
            id="hms-modules-title"
            className="wdev-section-head__title wdev-section-head__title--dark wdev-section-head__title--underline"
          >
            Module Features
          </h2>
          <p className="hms-modules__lead">
            Separate modules for admin, doctors, patients, nurses, pharmacists, lab technicians,
            accountants, and receptionists — each with role-specific functionalities.
          </p>
        </AnimatedSection>

        <div className="hms-modules__tabs" role="tablist" aria-label="Hospital management modules">
          {hmsModules.map((mod) => (
            <button
              key={mod.id}
              type="button"
              role="tab"
              aria-selected={activeId === mod.id}
              aria-controls={`hms-panel-${mod.id}`}
              id={`hms-tab-${mod.id}`}
              className={`hms-modules__tab ${activeId === mod.id ? "hms-modules__tab--active" : ""}`}
              onClick={() => setActiveId(mod.id)}
            >
              <HmsIcon name={mod.icon} className="hms-modules__tab-icon" />
              {mod.title.replace(" Module", "")}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeModule.id}
            id={`hms-panel-${activeModule.id}`}
            role="tabpanel"
            aria-labelledby={`hms-tab-${activeModule.id}`}
            className="hms-modules__panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            <div className="hms-modules__panel-head">
              <span className="hms-modules__panel-icon">
                <HmsIcon name={activeModule.icon} />
              </span>
              <div>
                <h3>{activeModule.title}</h3>
                <p>{activeModule.description}</p>
              </div>
            </div>

            <div className="hms-modules__features">
              {activeModule.features.map((feature) => (
                <FeatureBlock key={feature.title} feature={feature} />
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default HmsModulesSection;
